// src/components/contacts.jsx
// ContactsList - flattened list of all contacts across companies, with state
// filter and pagination at 50 per page (matches Companies tab for consistency).

import { useState, useEffect, useMemo, memo } from "react";
import { FI } from "./ui.jsx";
import { OwnerBadge } from "./owners.jsx";
import { ContactDetail, AddContactModal } from "./contact.jsx";

const PAGE_SIZE = 50;

// Memoized card so unchanged contacts don't re-render when ANY single
// contact changes. Same pattern as CompanyCard -- big perf win on mobile
// when there are thousands of contacts.
const ContactCard = memo(function ContactCard({ct, users, onClick}) {
  return (
    <div onClick={onClick}
      style={{background:"#fff",borderRadius:16,padding:20,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",cursor:"pointer",transition:"box-shadow .2s,transform .2s"}}
      onMouseEnter={e=>{e.currentTarget.style.boxShadow="var(--sh-lg)";e.currentTarget.style.transform="translateY(-2px)";}}
      onMouseLeave={e=>{e.currentTarget.style.boxShadow="var(--sh)";e.currentTarget.style.transform="none";}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
        <div style={{width:40,height:40,borderRadius:99,background:"linear-gradient(135deg,#b01020,#111111)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:"#fff",flexShrink:0}}>
          {ct.name[0].toUpperCase()}
        </div>
        <div>
          <div style={{fontWeight:600,fontSize:15,color:"var(--dark)"}}>{ct.name}</div>
          <div style={{fontSize:12,color:"var(--muted)"}}>{ct.role}</div>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:5,fontSize:12,color:"var(--muted)",marginBottom:12}}>
        <span>✉ {ct.email}</span>
        <span>🏢 <span style={{color:"var(--brown)",fontWeight:500}}>{ct.company.properties.name}</span></span>
        {ct.company.properties.state && (
          <span>📍 {ct.company.properties.city ? ct.company.properties.city + ", " : ""}{ct.company.properties.state}</span>
        )}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
        <span style={{color:"var(--muted)"}}>Owner:</span>
        <OwnerBadge ownerId={ct.ownerId} users={users}/>
      </div>
      <div style={{marginTop:10,fontSize:12,color:"var(--brown)",fontWeight:500}}>View contact →</div>
    </div>
  );
});

export function ContactsList({companies, users, currentUser, onUpdate, onOpenCompany}) {
  const [search,      setSearch]      = useState("");
  const [stateFilter, setStateFilter] = useState(() => {
    try { return localStorage.getItem("crm_ct_state_filter") || ""; } catch { return ""; }
  });
  const [page, setPage] = useState(0);
  const [sel,   setSel]   = useState(null);
  const [selCo, setSelCo] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  // Reset to page 0 when search or filter changes
  useEffect(() => { setPage(0); }, [search, stateFilter]);

  const setStateFilterPersist = (v) => {
    setStateFilter(v);
    try { localStorage.setItem("crm_ct_state_filter", v); } catch {}
  };

  // Flatten contacts and tag each with its company info -- memoized so we
  // don't recompute on every keystroke when only `search` changed.
  const all = useMemo(() => companies.flatMap(co =>
    (co.contacts || []).map(ct => ({...ct, company: co}))
  ), [companies]);

  // Build state options from contacts' parent companies (memoized)
  const {stateCounts, sortedStates} = useMemo(() => {
    const sc = {};
    for (const ct of all) {
      const s = (ct.company.properties.state || "").trim() || "(no state)";
      sc[s] = (sc[s] || 0) + 1;
    }
    return {
      stateCounts: sc,
      sortedStates: Object.keys(sc).sort((a, b) => a.localeCompare(b)),
    };
  }, [all]);

  // Filter pipeline (memoized)
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return all.filter(ct => {
      if (stateFilter) {
        const s = (ct.company.properties.state || "").trim() || "(no state)";
        if (s !== stateFilter) return false;
      }
      if (!q) return true;
      return ct.name.toLowerCase().includes(q) ||
             ct.email.toLowerCase().includes(q) ||
             (ct.role || "").toLowerCase().includes(q) ||
             ct.company.properties.name.toLowerCase().includes(q);
    });
  }, [all, search, stateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"flex-end"}}>
        <div style={{flex:"1 1 280px",minWidth:200}}>
          <FI placeholder="🔍 Search contacts..." value={search} onChange={setSearch}/>
        </div>
        <div style={{flex:"0 0 200px",minWidth:160,display:"flex",flexDirection:"column",gap:5}}>
          <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Filter by State</label>
          <select value={stateFilter} onChange={e => setStateFilterPersist(e.target.value)}
            style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid "+(stateFilter?"var(--brown)":"var(--warm2)"),background:stateFilter?"var(--warm1)":"#fff",fontSize:13,color:"var(--text)",outline:"none",fontFamily:"Barlow,sans-serif",cursor:"pointer"}}>
            <option value="">All states ({all.length})</option>
            {sortedStates.map(s => (
              <option key={s} value={s}>{s} ({stateCounts[s]})</option>
            ))}
          </select>
        </div>
        {stateFilter && (
          <button onClick={()=>setStateFilterPersist("")}
            style={{padding:"10px 16px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:12,color:"var(--muted)",cursor:"pointer",fontFamily:"Barlow,sans-serif",height:40,whiteSpace:"nowrap"}}
            title="Clear state filter">✕ Clear</button>
        )}
        <button onClick={()=>setShowAdd(true)}
          style={{padding:"10px 18px",borderRadius:10,border:"none",background:"var(--brown)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",height:40,whiteSpace:"nowrap",marginLeft:"auto"}}
          title="Add a new contact">+ New Contact</button>
      </div>

      {/* Active-filter banner */}
      {stateFilter && (
        <div style={{padding:"8px 14px",borderRadius:10,background:"var(--warm1)",border:"1px solid var(--warm2)",fontSize:12,color:"var(--muted)",marginBottom:14,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span>Showing</span>
          <strong style={{color:"var(--brown)"}}>{filtered.length}</strong>
          <span>of</span>
          <strong style={{color:"var(--brown)"}}>{all.length}</strong>
          <span>contacts —</span>
          <span style={{padding:"2px 10px",borderRadius:99,background:"var(--brown)",color:"#fff",fontWeight:600}}>state: {stateFilter}</span>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:14}}>
        {pageItems.map(ct => (
          <ContactCard key={ct.id} ct={ct} users={users} onClick={()=>{setSel(ct); setSelCo(ct.company);}}/>
        ))}
        {filtered.length === 0 && all.length > 0 && (
          <div style={{gridColumn:"1/-1",padding:60,textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>
            No contacts match your search and filter.
          </div>
        )}
        {all.length === 0 && (
          <div style={{gridColumn:"1/-1",padding:60,textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>
            No contacts found.
          </div>
        )}
      </div>

      {/* Pagination strip -- only when there's more than one page */}
      {filtered.length > PAGE_SIZE && (
        <div style={{marginTop:24,display:"flex",justifyContent:"center",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setPage(0)} disabled={safePage === 0}
            style={{padding:"7px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage===0?"var(--warm1)":"#fff",fontSize:12,color:safePage===0?"var(--warm3)":"var(--brown)",cursor:safePage===0?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>« First</button>
          <button onClick={()=>setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
            style={{padding:"7px 14px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage===0?"var(--warm1)":"#fff",fontSize:12,color:safePage===0?"var(--warm3)":"var(--brown)",cursor:safePage===0?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>‹ Prev</button>
          <span style={{padding:"7px 16px",fontSize:12,color:"var(--muted)",fontWeight:600}}>
            Page <strong style={{color:"var(--brown)"}}>{safePage + 1}</strong> of <strong style={{color:"var(--brown)"}}>{totalPages}</strong>
            <span style={{marginLeft:10,color:"var(--warm3)"}}>· {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          </span>
          <button onClick={()=>setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1}
            style={{padding:"7px 14px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage >= totalPages - 1?"var(--warm1)":"#fff",fontSize:12,color:safePage >= totalPages - 1?"var(--warm3)":"var(--brown)",cursor:safePage >= totalPages - 1?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>Next ›</button>
          <button onClick={()=>setPage(totalPages - 1)} disabled={safePage >= totalPages - 1}
            style={{padding:"7px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage >= totalPages - 1?"var(--warm1)":"#fff",fontSize:12,color:safePage >= totalPages - 1?"var(--warm3)":"var(--brown)",cursor:safePage >= totalPages - 1?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>Last »</button>
        </div>
      )}

      {sel && <ContactDetail
        contact={sel} company={selCo} users={users} currentUser={currentUser}
        onClose={()=>{setSel(null); setSelCo(null);}}
        onOpenCompany={co => {setSel(null); setSelCo(null); onOpenCompany(co);}}
      />}
      {showAdd && (
        <AddContactModal
          companies={companies}
          currentUser={currentUser}
          onClose={()=>setShowAdd(false)}
          onCreated={(newContact, parentCompanyId) => {
            // Find the parent company in our local list and append the new
            // contact to its contacts array. Propagate up via onUpdate so
            // App.jsx persists to localStorage + Azure Blob.
            const parentCo = companies.find(c => c.id === parentCompanyId);
            if (parentCo && onUpdate) {
              const updatedCo = {
                ...parentCo,
                contacts: [...(parentCo.contacts || []), newContact],
              };
              onUpdate(updatedCo);
              // Open the new contact's detail so the rep can verify
              setSel(newContact);
              setSelCo(updatedCo);
            }
          }}
        />
      )}
    </div>
  );
}
